/**
 * Structured buyer checklist content for AutoMapa.
 * TODO(M25+): Move to CMS / admin content editor at /admin/content/checklists when available.
 */

import { CHECKLIST_BUYER_ACTIONS, CHECKLIST_IMPORTANCE, CHECKLIST_PHASES, CHECKLIST_CATEGORIES } from "../constants/buyerChecklistConstants";
import { FAIR_PRICE_LABELS } from "../constants/automapFairPriceConstants";

/** @typedef {{ id: string, title: string, description: string, phase: string, importance: string, relatedRiskIndicators?: string[], relatedListingFields?: string[], buyerAction?: string, links?: { label: string, href: string }[], defaultVisible?: boolean, contextualOnly?: boolean }} BuyerChecklistItemDef */

/** @typedef {{ id: string, title: string, description: string, phase: string, items: BuyerChecklistItemDef[] }} BuyerChecklistSectionDef */

/** @type {BuyerChecklistItemDef[]} */
export const BUYER_CHECKLIST_ITEMS = [
  {
    id: "confirm_availability",
    key: "confirm_availability",
    category: CHECKLIST_CATEGORIES.DISPONIBILIDAD,
    title: "Confirmar que el vehículo sigue disponible",
    shortTitle: "Confirma disponibilidad",
    question: "Pregunta si el vehículo sigue disponible y si el precio publicado sigue vigente.",
    description: "Pide confirmación por WhatsApp antes de desplazarte o reservar tiempo.",
    whyItMatters: "La disponibilidad puede cambiar rápido en publicaciones usadas.",
    phase: CHECKLIST_PHASES.BEFORE_CONTACT,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.ASK_DEALER,
    relatedRiskIndicators: ["stale_listing", "availability_not_confirmed", "pending_confirmation"],
    relatedRiskFlags: ["stale_listing", "availability_not_confirmed"],
    defaultVisible: true,
    guideHref: "/guias/como-comprar-carro-usado",
  },
  {
    id: "confirm_price_valid",
    key: "confirm_price_valid",
    category: CHECKLIST_CATEGORIES.PRECIO,
    title: "Confirmar que el precio publicado sigue vigente",
    shortTitle: "Confirmar precio vigente",
    question: "Pregunta si el precio publicado sigue vigente y qué incluye.",
    description: "Confirma precio final, negociación y costos adicionales antes de visitar.",
    whyItMatters: "El precio en línea puede no incluir traspaso, garantías u otros cargos.",
    phase: CHECKLIST_PHASES.BEFORE_CONTACT,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.ASK_DEALER,
    relatedFairPriceLabels: [FAIR_PRICE_LABELS.high_price, FAIR_PRICE_LABELS.slightly_high],
    defaultVisible: true,
  },
  {
    id: "compare_price",
    key: "compare_price",
    category: CHECKLIST_CATEGORIES.COMPARACION,
    title: "Comparar el precio con vehículos similares",
    shortTitle: "Comparar precio",
    question: "Compara año, versión, kilometraje y ciudad con otras publicaciones similares.",
    description: "Revisa precio, versión, kilometraje y ciudad frente a otras publicaciones.",
    whyItMatters: "Te ayuda a negociar con referencia y detectar precios fuera de mercado.",
    phase: CHECKLIST_PHASES.BEFORE_CONTACT,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.COMPARE_PRICE,
    relatedRiskIndicators: ["price_outlier_high", "price_outlier_low", "price_unusually_high"],
    relatedRiskFlags: ["price_outlier_high", "price_outlier_low"],
    relatedFairPriceLabels: [
      FAIR_PRICE_LABELS.high_price,
      FAIR_PRICE_LABELS.slightly_high,
      FAIR_PRICE_LABELS.not_enough_data,
    ],
    defaultVisible: true,
  },
  {
    id: "verify_dealer_identity",
    title: "Verificar identidad y canal oficial del concesionario",
    description: "Confirma nombre comercial, ubicación y WhatsApp o teléfono oficial.",
    phase: CHECKLIST_PHASES.BEFORE_CONTACT,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.ASK_DEALER,
    relatedRiskIndicators: ["dealer_unverified", "dealer_verification_pending"],
    relatedListingFields: ["dealerUnverified"],
    defaultVisible: true,
  },
  {
    id: "request_more_photos",
    title: "Solicitar fotos adicionales si faltan",
    description: "Pide exterior, interior, motor, tablero y documentos visibles sin datos sensibles.",
    phase: CHECKLIST_PHASES.BEFORE_CONTACT,
    importance: CHECKLIST_IMPORTANCE.RECOMMENDED,
    buyerAction: CHECKLIST_BUYER_ACTIONS.ASK_DEALER,
    relatedRiskIndicators: ["missing_photos"],
    relatedListingFields: ["missingPhotos"],
    defaultVisible: true,
  },
  {
    id: "confirm_mileage",
    title: "Confirmar kilometraje",
    description: "Solicita evidencia del odómetro y coherencia con el año del vehículo.",
    phase: CHECKLIST_PHASES.BEFORE_CONTACT,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.ASK_DEALER,
    relatedRiskIndicators: ["missing_mileage", "mileage_unusually_high", "mileage_unusually_low", "report_suspicious_mileage"],
    relatedListingFields: ["missingMileage"],
    defaultVisible: true,
  },
  {
    id: "avoid_advance_payment",
    title: "Evitar pagos anticipados sin verificar",
    description: "No envíes dinero antes de ver el vehículo y revisar documentos básicos.",
    phase: CHECKLIST_PHASES.BEFORE_CONTACT,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.AVOID_PAYMENT,
    relatedRiskIndicators: ["price_unusually_low", "below_market_caution", "multiple_buyer_reports"],
    defaultVisible: true,
  },
  {
    id: "review_inconsistencies",
    title: "Revisar cuidadosamente la información",
    description: "Si algo no coincide, verifica antes de avanzar y reporta inconsistencias.",
    phase: CHECKLIST_PHASES.BEFORE_CONTACT,
    importance: CHECKLIST_IMPORTANCE.RECOMMENDED,
    buyerAction: CHECKLIST_BUYER_ACTIONS.REPORT_ISSUE,
    relatedRiskIndicators: ["multiple_buyer_reports", "report_vehicle_unavailable", "report_incorrect_price", "report_misleading_photos"],
    contextualOnly: true,
  },
  {
    id: "confirm_address_hours",
    title: "Confirmar dirección y horario de visita",
    description: "Acuerda lugar seguro, horario y persona de contacto en el concesionario.",
    phase: CHECKLIST_PHASES.BEFORE_VISIT,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.ASK_DEALER,
    defaultVisible: true,
  },
  {
    id: "prepare_questions",
    title: "Preparar preguntas sobre historial y mantenimiento",
    description: "Pregunta por servicios, accidentes declarados, llaves, manuales y accesorios incluidos.",
    phase: CHECKLIST_PHASES.BEFORE_VISIT,
    importance: CHECKLIST_IMPORTANCE.RECOMMENDED,
    buyerAction: CHECKLIST_BUYER_ACTIONS.ASK_DEALER,
    defaultVisible: true,
  },
  {
    id: "independent_inspection_offer",
    title: "Preguntar si acepta inspección independiente",
    description: "Un peritaje mecánico externo puede detectar problemas no visibles en fotos.",
    phase: CHECKLIST_PHASES.BEFORE_VISIT,
    importance: CHECKLIST_IMPORTANCE.RECOMMENDED,
    buyerAction: CHECKLIST_BUYER_ACTIONS.ASK_DEALER,
    defaultVisible: true,
  },
  {
    id: "match_listing_to_vehicle",
    title: "Verificar que el vehículo corresponda al anuncio",
    description: "Compara placa parcial, color, versión, kilometraje y equipamiento publicado.",
    phase: CHECKLIST_PHASES.INSPECTION,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.INSPECT_VEHICLE,
    defaultVisible: true,
  },
  {
    id: "test_drive",
    title: "Realizar test drive",
    description: "Prueba en ciudad y, si aplica, en pendiente. Escucha ruidos y revisa respuesta del motor.",
    phase: CHECKLIST_PHASES.INSPECTION,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.INSPECT_VEHICLE,
    defaultVisible: true,
  },
  {
    id: "mechanical_inspection",
    title: "Agendar inspección mecánica independiente",
    description: "Revisa motor, caja, frenos, suspensión, fugas, luces y aire acondicionado.",
    phase: CHECKLIST_PHASES.INSPECTION,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.INSPECT_VEHICLE,
    defaultVisible: true,
  },
  {
    id: "body_chassis_check",
    title: "Revisar carrocería y señales de choque o reparación",
    description: "Busca diferencias de pintura, soldaduras irregulares o piezas mal alineadas.",
    phase: CHECKLIST_PHASES.INSPECTION,
    importance: CHECKLIST_IMPORTANCE.RECOMMENDED,
    buyerAction: CHECKLIST_BUYER_ACTIONS.INSPECT_VEHICLE,
    defaultVisible: true,
  },
  {
    id: "tires_brakes",
    title: "Revisar llantas y frenos",
    description: "Desgaste desigual puede indicar problemas de alineación o suspensión.",
    phase: CHECKLIST_PHASES.INSPECTION,
    importance: CHECKLIST_IMPORTANCE.RECOMMENDED,
    buyerAction: CHECKLIST_BUYER_ACTIONS.INSPECT_VEHICLE,
    defaultVisible: false,
  },
  {
    id: "property_card",
    title: "Revisar tarjeta de propiedad / licencia de tránsito",
    description: "Verifica que los datos coincidan con el vehículo y el vendedor autorizado.",
    phase: CHECKLIST_PHASES.DOCUMENTS,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.VERIFY_DOCUMENT,
    defaultVisible: true,
  },
  {
    id: "soat_valid",
    title: "Verificar SOAT vigente",
    description: "Confirma vigencia y que corresponda al vehículo que vas a comprar.",
    phase: CHECKLIST_PHASES.DOCUMENTS,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.VERIFY_DOCUMENT,
    defaultVisible: true,
  },
  {
    id: "tecnomecanica_valid",
    title: "Verificar revisión técnico-mecánica vigente",
    description: "Cuando aplique, confirma que esté al día según normativa colombiana.",
    phase: CHECKLIST_PHASES.DOCUMENTS,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.VERIFY_DOCUMENT,
    defaultVisible: true,
  },
  {
    id: "fines_clear",
    title: "Consultar multas, comparendos y paz y salvo",
    description: "Pregunta por obligaciones pendientes antes de pagar o firmar.",
    phase: CHECKLIST_PHASES.DOCUMENTS,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.VERIFY_DOCUMENT,
    defaultVisible: true,
  },
  {
    id: "vehicle_taxes",
    title: "Revisar impuestos del vehículo",
    description: "Confirma situación de impuestos departamentales y municipales si aplica.",
    phase: CHECKLIST_PHASES.DOCUMENTS,
    importance: CHECKLIST_IMPORTANCE.RECOMMENDED,
    buyerAction: CHECKLIST_BUYER_ACTIONS.VERIFY_DOCUMENT,
    defaultVisible: false,
  },
  {
    id: "liens_limitations",
    title: "Verificar prenda o limitaciones de dominio",
    description: "Confirma que el vehículo pueda transferirse sin gravámenes ocultos.",
    phase: CHECKLIST_PHASES.DOCUMENTS,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.VERIFY_DOCUMENT,
    defaultVisible: true,
  },
  {
    id: "tradition_certificate",
    title: "Solicitar certificado de tradición, si aplica",
    description: "Útil para revisar historial de propietarios y anotaciones relevantes.",
    phase: CHECKLIST_PHASES.DOCUMENTS,
    importance: CHECKLIST_IMPORTANCE.RECOMMENDED,
    buyerAction: CHECKLIST_BUYER_ACTIONS.VERIFY_DOCUMENT,
    defaultVisible: false,
  },
  {
    id: "negotiate_inclusions",
    title: "Confirmar qué incluye el precio",
    description: "Traspaso, garantía ofrecida, llaves, accesorios, cambios menores o no.",
    phase: CHECKLIST_PHASES.NEGOTIATION,
    importance: CHECKLIST_IMPORTANCE.RECOMMENDED,
    buyerAction: CHECKLIST_BUYER_ACTIONS.ASK_DEALER,
    relatedRiskIndicators: ["fair_price_high", "price_unusually_high"],
    defaultVisible: false,
  },
  {
    id: "financing_costs",
    title: "Preguntar por financiación y costos adicionales",
    description: "Cuota inicial, plazo, seguros, gastos administrativos y condiciones reales.",
    phase: CHECKLIST_PHASES.NEGOTIATION,
    importance: CHECKLIST_IMPORTANCE.OPTIONAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.ASK_DEALER,
    defaultVisible: false,
  },
  {
    id: "formal_payment_channel",
    title: "Usar canales formales de pago",
    description: "Prefiere medios trazables acordados por escrito con el concesionario.",
    phase: CHECKLIST_PHASES.PAYMENT,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.AVOID_PAYMENT,
    defaultVisible: true,
  },
  {
    id: "no_personal_accounts",
    title: "Evitar pagos a cuentas personales no confirmadas",
    description: "Verifica titular y datos bancarios con el representante autorizado del negocio.",
    phase: CHECKLIST_PHASES.PAYMENT,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.AVOID_PAYMENT,
    defaultVisible: true,
  },
  {
    id: "payment_receipts",
    title: "Guardar soporte de pagos",
    description: "Conserva comprobantes, acuerdos y comunicaciones relevantes.",
    phase: CHECKLIST_PHASES.PAYMENT,
    importance: CHECKLIST_IMPORTANCE.RECOMMENDED,
    buyerAction: CHECKLIST_BUYER_ACTIONS.SAVE_NOTE,
    defaultVisible: false,
  },
  {
    id: "no_payment_without_docs",
    title: "No entregar dinero sin documentos claros",
    description: "Define qué documentos recibirás y cuándo antes de pagar el saldo.",
    phase: CHECKLIST_PHASES.PAYMENT,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.AVOID_PAYMENT,
    defaultVisible: true,
  },
  {
    id: "transfer_process",
    title: "Planear traspaso ante organismo de tránsito",
    description: "Confirma pasos, tiempos, costos y quién gestiona la transferencia.",
    phase: CHECKLIST_PHASES.TRANSFER,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.VERIFY_DOCUMENT,
    defaultVisible: true,
  },
  {
    id: "red_flag_pressure",
    title: "Desconfiar de presión para pagar rápido",
    description: "Un buen negocio permite tiempo para revisar documentos e inspección.",
    phase: CHECKLIST_PHASES.PAYMENT,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.AVOID_PAYMENT,
    relatedRiskIndicators: ["below_market_caution", "price_unusually_low"],
    contextualOnly: true,
  },
  {
    id: "red_flag_off_platform",
    title: "Evitar pagos por fuera sin respaldo",
    description: "Desconfía si te piden transferencias sin contrato ni datos verificables.",
    phase: CHECKLIST_PHASES.PAYMENT,
    importance: CHECKLIST_IMPORTANCE.ESSENTIAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.AVOID_PAYMENT,
    contextualOnly: true,
  },
  {
    id: "after_update_insurance",
    title: "Actualizar SOAT y seguros después de comprar",
    description: "Asegura coberturas vigentes a tu nombre tras el traspaso.",
    phase: CHECKLIST_PHASES.AFTER_PURCHASE,
    importance: CHECKLIST_IMPORTANCE.RECOMMENDED,
    buyerAction: CHECKLIST_BUYER_ACTIONS.SAVE_NOTE,
    defaultVisible: false,
  },
  {
    id: "after_maintenance_plan",
    title: "Programar mantenimiento inicial",
    description: "Cambia aceite, filtros o fluidos si no hay historial claro de servicios.",
    phase: CHECKLIST_PHASES.AFTER_PURCHASE,
    importance: CHECKLIST_IMPORTANCE.OPTIONAL,
    buyerAction: CHECKLIST_BUYER_ACTIONS.SAVE_NOTE,
    defaultVisible: false,
  },
];

/** Risk indicator → contextual checklist item overrides (title/description tweaks) */
export const RISK_CONTEXTUAL_OVERRIDES = {
  stale_listing: {
    title: "Confirma que el vehículo sigue disponible antes de desplazarte",
    description: "La disponibilidad no se ha confirmado recientemente en AutoMapa.",
  },
  pending_confirmation: {
    title: "Confirma que el vehículo sigue disponible antes de desplazarte",
    description: "La publicación indica disponibilidad pendiente de confirmar.",
  },
  availability_unknown: {
    title: "Pide confirmación por WhatsApp antes de visitar",
    description: "No hay confirmación clara de disponibilidad en la publicación.",
  },
  missing_photos: {
    title: "Solicita fotos adicionales del exterior, interior, motor y documentos visibles",
    description: "Esta publicación tiene pocas fotos para evaluar el estado.",
  },
  missing_mileage: {
    title: "Confirma kilometraje y solicita evidencia antes de negociar",
    description: "El kilometraje no está publicado o está incompleto.",
  },
  price_unusually_low: {
    title: "Revisa documentos, historial, estado mecánico y evita pagos anticipados",
    description: "El precio está por debajo de lo usual para vehículos similares.",
  },
  below_market_caution: {
    title: "Revisa documentos, historial, estado mecánico y evita pagos anticipados",
    description: "El precio aparece por debajo del mercado observado en AutoMapa.",
  },
  price_unusually_high: {
    title: "Compara versión, kilometraje, estado y beneficios antes de aceptar el precio",
    description: "El precio está por encima de publicaciones similares.",
  },
  fair_price_high: {
    title: "Compara versión, kilometraje, estado y beneficios antes de aceptar el precio",
    description: "AutoMapa detecta un precio alto frente a comparables.",
  },
  dealer_unverified: {
    title: "Verifica identidad, ubicación y canal oficial del concesionario",
    description: "Este concesionario aún no tiene verificación completa en AutoMapa.",
  },
  dealer_verification_pending: {
    title: "Verifica identidad, ubicación y canal oficial del concesionario",
    description: "La verificación del concesionario está en proceso.",
  },
  multiple_buyer_reports: {
    title: "Revisa cuidadosamente la información y reporta cualquier inconsistencia",
    description: "Hay reportes de compradores que conviene tener en cuenta.",
  },
  report_vehicle_unavailable: {
    title: "Confirma disponibilidad actual antes de avanzar",
    description: "Compradores han reportado posibles problemas de disponibilidad.",
  },
  report_incorrect_price: {
    title: "Confirma precio final y condiciones por escrito",
    description: "Se han reportado diferencias entre precio publicado y cotizado.",
  },
  report_misleading_photos: {
    title: "Solicita fotos actuales y verifica el vehículo en persona",
    description: "Se han reportado posibles diferencias en las fotos.",
  },
  report_suspicious_mileage: {
    title: "Confirma kilometraje con evidencia del odómetro",
    description: "Se han reportado dudas sobre el kilometraje publicado.",
  },
  fair_price_not_enough_data: {
    title: "Compara manualmente con otras publicaciones similares",
    description: "AutoMapa no tiene suficientes comparables para estimar el precio.",
  },
  low_quality_score: {
    title: "Solicita información completa antes de visitar",
    description: "La publicación tiene datos incompletos.",
  },
  listing_quality_low: {
    title: "Solicita información adicional antes de visitar",
    description: "Esta publicación tiene información limitada.",
  },
  availability_not_confirmed: {
    title: "Confirma que el vehículo sigue disponible antes de desplazarte",
    description: "La disponibilidad no está confirmada recientemente.",
  },
  price_outlier_high: {
    title: "Compara versión, kilometraje, estado y beneficios antes de aceptar el precio",
    description: "El precio está por encima de publicaciones similares.",
  },
  price_outlier_low: {
    title: "Pregunta por estado, documentos e historial del vehículo",
    description: "El precio está por debajo de lo usual para vehículos similares.",
  },
  trust_safety_report_open: {
    title: "Revisa con cuidado y considera un peritaje",
    description: "Esta publicación tiene información en revisión en AutoMapa.",
  },
  dealer_contact_missing: {
    title: "Espera confirmación de contacto antes de avanzar",
    description: "El canal de contacto del concesionario no está completo.",
  },
  few_photos: {
    title: "Pide fotos adicionales del exterior, interior y odómetro",
    description: "Esta publicación tiene pocas fotos para evaluar el estado.",
  },
};

/** Fair-price label → contextual checklist overrides */
export const FAIR_PRICE_CONTEXTUAL_OVERRIDES = {
  [FAIR_PRICE_LABELS.high_price]: {
    title: "Compara opciones similares antes de decidir",
    description: "AutoMapa detecta un precio alto frente a comparables.",
  },
  [FAIR_PRICE_LABELS.slightly_high]: {
    title: "Pregunta qué justifica el precio publicado",
    description: "El precio está un poco por encima de publicaciones similares.",
  },
  [FAIR_PRICE_LABELS.below_market]: {
    title: "Pregunta por estado, documentos e historial del vehículo",
    description: "Un precio muy bajo conviene revisarlo con más preguntas.",
  },
  [FAIR_PRICE_LABELS.not_enough_data]: {
    title: "Compara manualmente por año, kilometraje y versión",
    description: "AutoMapa no tiene suficientes comparables para este vehículo.",
  },
  [FAIR_PRICE_LABELS.fair_price]: {
    title: "Aun con precio justo, confirma estado y documentos",
    description: "Un precio razonable no reemplaza revisión mecánica y documental.",
  },
};

/** Hub page section groupings */
export const BUYER_CHECKLIST_HUB_SECTIONS = [
  {
    id: "before_contact",
    title: "Antes de contactar",
    description: "Verifica lo básico antes de escribir al concesionario.",
    phase: CHECKLIST_PHASES.BEFORE_CONTACT,
    itemIds: ["confirm_availability", "compare_price", "verify_dealer_identity", "request_more_photos", "confirm_mileage", "avoid_advance_payment"],
  },
  {
    id: "before_visit",
    title: "Antes de visitar",
    description: "Organiza la visita con claridad y seguridad.",
    phase: CHECKLIST_PHASES.BEFORE_VISIT,
    itemIds: ["confirm_address_hours", "prepare_questions", "independent_inspection_offer"],
  },
  {
    id: "during_visit",
    title: "Durante la visita",
    description: "Comprueba que lo que ves coincide con lo publicado.",
    phase: CHECKLIST_PHASES.INSPECTION,
    itemIds: ["match_listing_to_vehicle", "test_drive", "body_chassis_check"],
  },
  {
    id: "mechanical",
    title: "Revisión mecánica",
    description: "Un peritaje independiente reduce sorpresas costosas.",
    phase: CHECKLIST_PHASES.INSPECTION,
    itemIds: ["mechanical_inspection", "tires_brakes"],
  },
  {
    id: "documents",
    title: "Documentos",
    description: "Revisa papeles antes de negociar el cierre.",
    phase: CHECKLIST_PHASES.DOCUMENTS,
    itemIds: ["property_card", "soat_valid", "tecnomecanica_valid", "fines_clear", "vehicle_taxes", "liens_limitations", "tradition_certificate"],
  },
  {
    id: "soat_tecno",
    title: "SOAT y tecnomecánica",
    description: "Obligatorios para circular legalmente en Colombia.",
    phase: CHECKLIST_PHASES.DOCUMENTS,
    itemIds: ["soat_valid", "tecnomecanica_valid"],
  },
  {
    id: "transfer",
    title: "Traspaso",
    description: "Planifica la transferencia ante tránsito.",
    phase: CHECKLIST_PHASES.TRANSFER,
    itemIds: ["transfer_process"],
  },
  {
    id: "payment",
    title: "Pago seguro",
    description: "Protege tu dinero con acuerdos claros.",
    phase: CHECKLIST_PHASES.PAYMENT,
    itemIds: ["formal_payment_channel", "no_personal_accounts", "no_payment_without_docs", "payment_receipts", "red_flag_pressure", "red_flag_off_platform"],
  },
  {
    id: "red_flags",
    title: "Señales de alerta",
    description: "Detente y verifica si observas estas señales.",
    phase: CHECKLIST_PHASES.PAYMENT,
    itemIds: ["red_flag_pressure", "red_flag_off_platform", "avoid_advance_payment"],
  },
  {
    id: "after_purchase",
    title: "Después de comprar",
    description: "Cierra pendientes post-compra.",
    phase: CHECKLIST_PHASES.AFTER_PURCHASE,
    itemIds: ["after_update_insurance", "after_maintenance_plan"],
  },
];

const itemMap = new Map(BUYER_CHECKLIST_ITEMS.map((item) => [item.id, item]));

export function getChecklistItemById(id) {
  return itemMap.get(id) || null;
}

export function getChecklistItemsForSection(section) {
  return (section.itemIds || [])
    .map((id) => getChecklistItemById(id))
    .filter(Boolean);
}

export function getAllChecklistItemIds() {
  return BUYER_CHECKLIST_ITEMS.map((item) => item.id);
}

export function getChecklistSectionsWithItems() {
  return BUYER_CHECKLIST_HUB_SECTIONS.map((section) => ({
    ...section,
    items: getChecklistItemsForSection(section),
  }));
}
